import { useState } from 'react'
import { Text, View } from 'react-native'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useRouter } from 'expo-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { SELLER_COMPLAINT_BUY_ORDER_AMOUNT } from '@/constants/graphql/mutations'
import { GET_BUY_ORDER_CHANGE_AMOUNT_UPLOADS_PRESIGNED } from '@/constants/graphql/queries'
import { formatLocalFiatAmount, marketPresentation } from '@/config/marketPresentation'
import { AddImageInterface, BuyOrder, ImageAsset } from '@/types/common'
import { CurrencyKind } from '@/types/wallet'
import { fundsPinErrorMessage } from '@/utils/fundsPinError'
import { preparePickedImageUpload } from '@/utils/imageUpload'
import { launchMediaLibraryAsync } from '@/utils/mediaPicker'
import { generateRandomFileName, uploadImageToPresigned } from '@/utils/url'
import {
  Button,
  FormScrollView,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  useModal,
  useToast
} from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { NumberInput, TxInput } from '@/components/form-control'
import { UploadImage } from '@/components/icon'
import { ImageButton } from '@/components/ImageButton'
import { useImageViewer } from '@/components/ImageViewer'
import { useLoadingIndicator } from '@/components/LoadingIndicator'

type formValues = {
  amount: number
  withdrawalPassword: string
}

const defaultValues: formValues = {
  amount: 0,
  withdrawalPassword: ''
}
export function AmountMismatchForm({
  data: orderData,
  completeAppeal
}: {
  data: BuyOrder
  completeAppeal: () => void
}) {
  const loading = useLoadingIndicator()
  const { t } = useTranslation()
  const router = useRouter()
  const { bottom } = useSafeAreaInsets()
  const [images, setImages] = useState<ImageAsset[]>([])

  // const scrollViewHeight = windowHeight - top - 44 - bottom + 72

  const { openModal, closeModal } = useModal()
  const { control, handleSubmit, watch, setError } = useForm<formValues>({ defaultValues })
  const { setSource, onDeleteCallback } = useImageViewer()
  const toast = useToast()
  // const [permission, requestPermission] = ImagePicker.useMediaLibraryPermissions()
  const amount = watch('amount')
  const withdrawalPassword = watch('withdrawalPassword')

  // useEffect(() => {
  //   if (!permission || permission.granted) {
  //     return
  //   }
  //   if (permission.canAskAgain) {
  //     requestPermission()
  //   }
  // }, [permission, requestPermission])

  const [submitComplaint] = useMutation(SELLER_COMPLAINT_BUY_ORDER_AMOUNT, {
    onCompleted: () => {
      completeAppeal()
    },
    onError: (error) => {
      loading.stop()
      console.error('buyerUploadPaymentProof error:', error)
      const { graphQLErrors } = error
      const pinErrorMessage = fundsPinErrorMessage(error)
      if (pinErrorMessage) {
        setError('withdrawalPassword', { message: pinErrorMessage })
        return
      }
      const errorCode = graphQLErrors[0]?.extensions?.code
      if (errorCode === 2000) {
        setError('amount', { message: t('orderAppeal:amountMismatch.amountExceedsRemaining') })
      } else if (errorCode === 2017 || errorCode === 2006) {
        openModal({
          children: (
            <ModalContent
              closeModal={closeModal}
              text={
                errorCode === 2017
                  ? t('orderAppeal:amountMismatch.changeInProgress')
                  : t('orderAppeal:amountMismatch.exceedsOriginal')
              }
            />
          ),
          onClose: closeModal
        })
      }
    }
  })

  const [getUploadsPresigned] = useLazyQuery(GET_BUY_ORDER_CHANGE_AMOUNT_UPLOADS_PRESIGNED, {
    onError: (error) => console.error(error)
  })

  const addImage: AddImageInterface = (
    uri: string,
    _index: number,
    fileName?: string | null,
    mimeType?: string | null
  ) => {
    const extension = typeof fileName === 'string' ? fileName.split('.').at(-1) : 'jpg'
    const name = `${generateRandomFileName()}.${extension}`
    const asset = {
      uri,
      fileName: name,
      mimeType
    }

    const nextImages = [...images]
    if (!nextImages[0]) {
      nextImages[0] = asset
    } else if (!nextImages[1]) {
      nextImages[1] = asset
    } else {
      nextImages[2] = asset
    }
    setImages(nextImages)
  }

  const deleteImage = (deleteIndex: number) => {
    setImages([...images.filter((_, index) => index !== deleteIndex)])
  }

  async function pickImage() {
    const result = await launchMediaLibraryAsync({
      mediaTypes: ['images'],
      quality: 1,
      allowsMultipleSelection: false
    })

    if (!result.canceled) {
      const prepared = await preparePickedImageUpload(result.assets[0])
      if (!prepared.ok) {
        toast.show({
          variant: 'error',
          description: prepared.reason === 'type' ? t('orders:imageFormatInvalid') : t('orders:imageTooLarge')
        })
        return
      }
      addImage(prepared.asset.uri, 0, prepared.asset.fileName, prepared.asset.mimeType)
    }
  }

  const handleViewAddImage = (index: number) => {
    if (images[index]) {
      setSource(images[index].uri)
      onDeleteCallback.current = () => deleteImage(index)
    } else {
      pickImage()
    }
  }

  const startSubmission = async (formData: formValues) => {
    // closeModal()
    loading.start()

    let fileNames
    try {
      const responses = await Promise.all(
        images.map(({ fileName }) => {
          return getUploadsPresigned({
            variables: {
              buyOrderId: orderData.id,
              fileName
            }
          })
        })
      )
      // Find images
      fileNames = responses.map((response) => {
        const uploadUrl = response.data.getBuyOrderChangeAmountUploadsPresigned
        const urlObject = new URL(uploadUrl)
        const paths = urlObject.pathname.split('/')
        return decodeURIComponent(paths[paths.length - 1])
      })
      // Upload images
      await Promise.all(
        fileNames.map((fileName, index) => {
          return uploadImageToPresigned(
            images[index],
            fileName,
            responses[index].data.getBuyOrderChangeAmountUploadsPresigned
          )
        })
      )
    } catch (e) {
      loading.stop()
      console.log(e)
      console.log('fail to upload images!')
      toast.show({ variant: 'error', description: t('auth:toast.error.timeoutError') })
      return
    }

    submitComplaint({
      variables: {
        buyOrderId: orderData.id,
        updateAmount: {
          kind: CurrencyKind[CurrencyKind.YBB],
          value: formData.amount.toString()
        },
        changeAmountProofFileName: images.map((image) => image.fileName),
        withdrawalPassword: formData.withdrawalPassword
      }
    })
  }

  const handleForgotPassword = () => {
    router.push('/setting/tx-password')
  }

  return (
    <View className={'flex-1 bg-background-secondary'}>
        <FormScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom + 72 }}>
          <View className={'bg-background-primary px-4 pb-4'}>
            <View className={'mx-[-16px] border-b border-border-10 pb-[15px] pt-4'}>
              <Text className={'mx-4 text-sm text-text-secondary'}>
                {t('orderAppeal:amountMismatch.description', { token: marketPresentation.platformToken.displayName })}
              </Text>
            </View>
            <View className={'flex-row justify-between pb-8 pt-4'}>
              <Text className={'text-sm text-text-secondary'}>{t('orderAppeal:amountMismatch.estimatedAmount')}</Text>
              <Text
                className={'mx-4 font-roboto-medium text-sm text-text-primary'}
              >{formatLocalFiatAmount(orderData.originDealAmount.value)}</Text>
            </View>
            <NumberInput
              control={control}
              name={'amount'}
              label={t('orderAppeal:amountMismatch.actualAmount')}
              type={'number'}
              autoCorrect={false}
              returnKeyType={'done'}
              keyboardType={'decimal-pad'}
              leftElement={<Text className={'ml-3 text-sm text-text-secondary'}>{marketPresentation.localFiat.symbol}</Text>}
            />

            <View className={'gap-1'}>
              <Text className={'text-sm font-medium text-text-secondary'}>{t('orderAppeal:amountMismatch.uploadImage')}</Text>
              <View className={'flex-row gap-2'}>
                {[0, 1, 2].map((index) => (
                  <ImageButton
                    key={index}
                    image={images[index]?.uri}
                    onPress={() => handleViewAddImage(index)}
                    blankElement={<UploadImage className={'size-6 text-text-quaternary'} />}
                  />
                ))}
              </View>
              <Text className={'text-sm text-text-secondary'}>{t('orderAppeal:amountMismatch.uploadImageTip')}</Text>
            </View>
            <View className={'h-6'} />
            <TxInput control={control} name={'withdrawalPassword'} onForgotPassword={handleForgotPassword} />
          </View>
        </FormScrollView>
        <BottomActionPanel>
          <Button
            className={'flex-1'}
            size={'md'}
            disabled={!amount || !withdrawalPassword || !images[0] || loading.isLoading}
            onPress={handleSubmit(startSubmission)}
          >
            {t('orderAppeal:submitButton.amountMismatch')}
          </Button>
        </BottomActionPanel>
    </View>
  )
}

function ModalContent({ closeModal, text }: { closeModal: () => void; text: string }) {
  const { t } = useTranslation()
  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('orderAppeal:amountMismatch.limitTitle')}</ModalTitle>
      </ModalHeader>
      <View className={'px-4 py-2'}>
        <Text className={'text-center text-sm text-text-secondary'}>{text}</Text>
      </View>
      <ModalFooter>
        <Button className={'flex-1'} size={'md'} onPress={closeModal}>
          {t('ui:gotIt')}
        </Button>
      </ModalFooter>
    </>
  )
}
